// @ts-nocheck
import invariant from 'tiny-invariant'
import warning from 'tiny-warning'
import createTransitionManager from './createTransitionManager'
import { canUseDOM } from './DOMUtils'
import { createLocation } from './LocationUtils'
import { addLeadingSlash, createPath, hasBasename, stripBasename, stripTrailingSlash } from './PathUtils'
import { BrowserHistory, History, HistoryState, Location } from './types'

const preD = 'XXX createBrowserHistory.ts'
const bDebug = false

function getHistoryState(): HistoryState {
  try {
    return window.history.state || {}
  } catch (e) {
    // IE 11 sometimes throws when accessing window.history.state
    // See https://github.com/ReactTraining/history/pull/289
    return {}
  }
}

export function createBrowserHistory(props: BrowserHistory = {}): History {
  invariant(canUseDOM, 'Browser history needs a DOM')

  const globalHistory = window.history
  const { forceRefresh = false } = props

  const basename = props.basename ? stripTrailingSlash(addLeadingSlash(props.basename)) : ''

  function getDOMLocation(historyState: HistoryState) {
    const { key, state } = historyState || {}
    const { pathname, search, hash } = window.location

    let path = pathname + search + hash

    warning(
      !basename || hasBasename(path, basename),
      `You are attempting to use a basename on a page whose URL path does not begin ` +
        `with the basename. Expected path "${path}" to begin with "${basename}".`
    )

    // Only strip the basename, no URL parts like protocol or host
    if (basename) path = stripBasename(path, basename)

    // Strip out protocol and host if included
    if (path.includes('http://') || path.includes('https://')) {
      const url = new URL(path, window.location.origin) // Safely create URL object
      path = url.pathname + url.search + url.hash // Ensure only the path is kept
    }

    return createLocation(path, state, key)
  }

  function createKey(): string {
    return Math.random().toString(36).substr(2, 6)
  }

  const transitionManager = createTransitionManager()

  function setState(nextState) {
    Object.assign(history, nextState)
    history.length = globalHistory.length

    bDebug && console.log(`${preD} setState`, { nextState, history })

    transitionManager.notifyListeners(history.location, history.action)
  }

  function handlePop(location) {
    setState({ action: 'POP', location })
  }

  const initialLocation = getDOMLocation(getHistoryState())
  // let allKeys = [initialLocation.key]

  // Public interface

  function createHref(location) {
    // Fix: Create the path properly and ensure no full URL is created
    return basename + createPath(location) // Ensure only path-related components are included
  }

  function push(path: string | Location): void {
    let location = typeof path === 'string' ? createLocation(path, null, createKey(), history.location) : path

    // Fix: Ensure `path` is not a full URL
    if (typeof path === 'string' && (path.includes('http://') || path.includes('https://'))) {
      const url = new URL(path)
      location = createLocation(url.pathname + url.search + url.hash, null, createKey(), history.location)
    }

    const href = createHref(location)
    const { key, state } = location

    globalHistory.pushState({ key, state }, null, href)

    bDebug && console.log(`${preD} push`, { path, location, key, href, state, globalHistory, forceRefresh })

    if (forceRefresh) {
      bDebug && console.log(`${preD} push #1`, {})
      window.location.href = href
    } else {
      // Only pass the pathname and necessary details to notifyListeners
      transitionManager.notifyListeners(location, 'PUSH')
    }
  }

  function go(n) {
    globalHistory.go(n)
  }

  function goBack() {
    go(-1)
  }

  function goForward() {
    go(1)
  }

  let listenerCount = 0

  function checkDOMListeners(delta) {
    listenerCount += delta

    if (listenerCount === 1 && delta === 1) {
      window.addEventListener('popstate', function (event) {
        handlePop(getDOMLocation(event.state))
      })
      window.addEventListener('hashchange', function () {
        handlePop(getDOMLocation(getHistoryState()))
      })
    } else if (listenerCount === 0) {
      window.removeEventListener('popstate', function (event) {
        handlePop(getDOMLocation(event.state))
      })
      window.removeEventListener('hashchange', function () {
        handlePop(getDOMLocation(getHistoryState()))
      })
    }
  }

  function listen(listener) {
    const unlisten = transitionManager.appendListener(listener)
    checkDOMListeners(1)

    return () => {
      checkDOMListeners(-1)
      unlisten()
    }
  }
  const history: History = {
    length: globalHistory.length,
    action: 'POP',
    initialLocation,
    createHref,
    push,
    // replace,
    go,
    goBack,
    goForward,
    listen,
  }

  return history
}

export default createBrowserHistory
